/**
 * Bifrost errors as Excel errors.
 *
 * The functions build an error as text (`#ERR: …`, formatCellError). Returned
 * as text it reads in a plain cell but turns into a bare #VALUE! the moment a
 * cell does arithmetic with it — `=-BIFROST.LEDGER(…)`, a variance column —
 * and the reason is gone. As an Excel error (#VALUE! with the message) it
 * propagates the way Excel users expect, works with IFERROR, and Excel shows
 * the message on the cell's error indicator.
 *
 * Only where Excel shows that message: Excel on the web does, Excel for Mac
 * shows a bare #VALUE! and its own "Error in Value" (verified 2026-10-24), so
 * on Mac — and anywhere unknown, or without CustomFunctions.Error — the text
 * stays, which at least reads in the cell. Windows follows Microsoft's
 * documentation that the message is shown.
 */

const PREFIX = '#ERR: ';

/** Office.context.diagnostics.platform values where Excel shows a custom error's message. */
const MESSAGE_PLATFORMS: ReadonlySet<string> = new Set(['OfficeOnline', 'PC']);

interface ExcelErrorRuntime {
  Error: new (code: string, message?: string) => unknown;
  ErrorCode: { invalidValue: string };
}

declare const CustomFunctions: Partial<ExcelErrorRuntime> | undefined;

function platform(): string | null {
  try {
    return typeof Office === 'undefined' ? null : String(Office.context?.diagnostics?.platform ?? '') || null;
  } catch {
    return null;
  }
}

function runtime(): ExcelErrorRuntime | null {
  if (typeof CustomFunctions === 'undefined' || !CustomFunctions?.Error || !CustomFunctions.ErrorCode) return null;
  const where = platform();
  if (!where || !MESSAGE_PLATFORMS.has(where)) return null;
  return CustomFunctions as ExcelErrorRuntime;
}

/** The message of a Bifrost error text, or null when the value is not one. */
export function errorMessageOf(value: unknown): string | null {
  return typeof value === 'string' && value.startsWith(PREFIX) ? value.slice(PREFIX.length) : null;
}

function rethrowAsExcelError(value: unknown): void {
  const message = errorMessageOf(value);
  const excel = runtime();
  if (message !== null && excel) throw new excel.Error(excel.ErrorCode.invalidValue, message);
}

/** A value function whose Bifrost errors reach Excel as #VALUE! with their message. */
export function withExcelErrors<A extends unknown[], R>(fn: (...args: A) => Promise<R>): (...args: A) => Promise<R> {
  return async (...args: A) => {
    const value = await fn(...args);
    rethrowAsExcelError(value);
    return value;
  };
}

/** The same for a spilled function: an error comes back as a one-cell array. */
export function withExcelErrorsMatrix<A extends unknown[], R extends unknown[][]>(fn: (...args: A) => Promise<R>): (...args: A) => Promise<R> {
  return async (...args: A) => {
    const value = await fn(...args);
    if (value.length === 1 && value[0]?.length === 1) rethrowAsExcelError(value[0][0]);
    return value;
  };
}

/**
 * An Excel error argument, rebuilt so the cell shows the same error the
 * argument carried (#N/A stays #N/A). Falls back to the argument itself.
 */
export function excelErrorLike(error: { code?: string }): unknown {
  if (typeof CustomFunctions === 'undefined' || !CustomFunctions?.Error) return error;
  return new CustomFunctions.Error(error.code ?? CustomFunctions.ErrorCode?.invalidValue ?? '#VALUE!');
}
